import { useMemberChecklist } from "@circle-react/components/MemberChecklist/hooks/useMemberChecklist";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import {
  isCommunityAdmin,
  isCommunityModeratorButNotAdmin,
} from "@circle-react/helpers/communityMemberHelpers";
import { communitiesPaths } from "@circle-react/helpers/routerPathHelpers";
import { signOutPath } from "@circle-react/helpers/urlHelpers";
import type { PathWithState } from "@circle-react/helpers/urlHelpers/memberProfileModalPaths";
import { useCanSwitchCommunities } from "@circle-react/hooks/community_switcher/useCanSwitchCommunities";
import { useIsFeatureFlagEnabled } from "@circle-react/hooks/UseCommunitySetting";
import { useIsConnectEnabled } from "@circle-react/hooks/useIsConnectEnabled";
import { IconButton } from "@circleco/compass/components/IconButton";
import {
  useNavigate,
  useLocation,
  type HistoryState,
} from "@tanstack/react-router";
import { useState } from "react";

import { t } from "@/i18n-js/instance";
import { useProfileDrawer } from "@/react/components/Drawers/ProfileDrawer";
import { useMobileDrawerOverride } from "@/react/components/Mobile/hooks/topBar/useMobileDrawerOverride";
import { useHideOnboardingConfirmationModal } from "@/react/components/Modals/HideOnboardingConfirmationModal";
import { useChecklistAvailable } from "@/react/hooks/useChecklistAvailable";
import { useElectronAPI } from "@/react/hooks/utils/useElectronAPI";

import { DrawerCloseAllContext } from "../components/DrawerCloseAllContext";
import { DrawerDivider } from "../components/DrawerDivider";
import { DrawerMenuItem } from "../components/DrawerMenuItem";
import { ProfileActionsSection } from "./ProfileActionsSection";
import { ProfileHeaderButton } from "./ProfileHeaderButton";
import { SettingsNavigationSection } from "./SettingsNavigationSection";

interface MobileNavDrawerContentProps {
  /** Force-close the entire nav drawer (and any nested drawers). */
  onClose?: () => void;
}

/**
 * Profile/navigation drawer content for the mobile shell.
 */
export const MobileNavDrawerContent = ({
  onClose,
}: MobileNavDrawerContentProps) => {
  const { isElectron, electronAPI } = useElectronAPI();
  const location = useLocation({
    select: location => ({
      pathname: location.pathname,
      search: location.searchStr,
    }),
  });
  const navigate = useNavigate();
  const { drawerOverride } = useMobileDrawerOverride();
  const { currentCommunityMember } = usePunditUserContext();
  const { show: showMemberProfileDrawer } = useProfileDrawer();
  const [isAppearanceOpen, setIsAppearanceOpen] = useState(false);
  const [isCommunitySwitcherOpen, setIsCommunitySwitcherOpen] = useState(false);
  const [isThreadsOpen, setIsThreadsOpen] = useState(false);
  const isConnectEnabled = useIsConnectEnabled();
  const canSwitchCommunities = useCanSwitchCommunities();
  const isUnifiedInboxMobileViewEnabled = useIsFeatureFlagEnabled(
    "live_pulse_unified_inbox_mobile_view_enabled",
  );

  const {
    shouldShowMemberChecklist,
    isMemberChecklistOpen,
    handleMemberChecklistOpenChange,
  } = useMemberChecklist({ autoOpen: false });
  const { isChecklistAvailable } = useChecklistAvailable();
  const hideOnboardingModal = useHideOnboardingConfirmationModal();

  if (!currentCommunityMember) return null;

  const {
    avatar_url: avatarUrl,
    name,
    public_uid: publicUid,
    email,
  } = currentCommunityMember;

  const {
    can_access_billing: canAccessBilling,
    can_access_authentication_page: canAccessAuthentication,
    can_show_paywalls_affiliate: canShowAffiliate,
  } = currentCommunityMember.policies || {};

  const isAdmin = isCommunityAdmin(currentCommunityMember);
  const isModeratorButNotAdmin = isCommunityModeratorButNotAdmin(
    currentCommunityMember,
  );

  /** Force-close every drawer in the stack (nested drawers + nav). */
  const closeAll = () => {
    setIsAppearanceOpen(false);
    setIsCommunitySwitcherOpen(false);
    setIsThreadsOpen(false);
    handleMemberChecklistOpenChange(false);
    onClose?.();
  };

  /** Navigate and force-close the entire drawer stack. */
  const navigateAndClose = (path: string | PathWithState) => {
    if (typeof path === "string") {
      void navigate({ to: path });
    } else {
      void navigate({
        to: path.pathname,
        state: path.state as HistoryState,
      });
    }
    closeAll();
  };

  const handleSignOut = () => {
    try {
      if (isElectron) {
        electronAPI?.logout?.();
      }
    } finally {
      window.location.href = signOutPath();
    }
  };

  return (
    <div className="flex flex-col">
      {/* Profile header */}
      <ProfileHeaderButton
        name={name}
        avatarUrl={avatarUrl}
        email={email}
        onClick={() => {
          void showMemberProfileDrawer({ memberPublicId: publicUid ?? "" });
          onClose?.();
        }}
      />

      {/* Page-specific section */}
      {drawerOverride && (
        <>
          <DrawerCloseAllContext.Provider value={closeAll}>
            <div className="flex flex-col gap-0.5">{drawerOverride}</div>
          </DrawerCloseAllContext.Provider>
          <DrawerDivider />
        </>
      )}

      <div className="flex flex-col gap-0.5">
        {isChecklistAvailable && (
          <DrawerMenuItem
            icon="to-do-list"
            label={t("getting_started")}
            onClick={() => navigateAndClose(communitiesPaths.onboarding())}
            trailingAction={
              <IconButton
                icon="cross"
                variant="outline"
                size="sm"
                aria-label={t("close")}
                onClick={() => {
                  void hideOnboardingModal.show();
                  closeAll();
                }}
              />
            }
          />
        )}
        <ProfileActionsSection
          isThreadsOpen={isThreadsOpen}
          setIsThreadsOpen={setIsThreadsOpen}
          isConnectEnabled={isConnectEnabled}
          shouldShowMemberChecklist={shouldShowMemberChecklist}
          isMemberChecklistOpen={isMemberChecklistOpen}
          handleMemberChecklistOpenChange={handleMemberChecklistOpenChange}
          closeAll={closeAll}
          navigateAndClose={navigateAndClose}
          location={location}
        />
      </div>

      <DrawerDivider />

      <SettingsNavigationSection
        isAppearanceOpen={isAppearanceOpen}
        setIsAppearanceOpen={setIsAppearanceOpen}
        isCommunitySwitcherOpen={isCommunitySwitcherOpen}
        setIsCommunitySwitcherOpen={setIsCommunitySwitcherOpen}
        canAccessAuthentication={canAccessAuthentication}
        canAccessBilling={canAccessBilling}
        canShowAffiliate={canShowAffiliate}
        canSwitchCommunities={canSwitchCommunities}
        canAccessInbox={isAdmin && isUnifiedInboxMobileViewEnabled}
        isAdmin={isAdmin}
        isModeratorButNotAdmin={isModeratorButNotAdmin}
        closeAll={closeAll}
        navigateAndClose={navigateAndClose}
        location={location}
      />

      <DrawerDivider />

      {/* Sign out */}
      <div className="flex flex-col gap-0.5">
        <DrawerMenuItem
          icon="arrow-box-left"
          label={t("sign_out")}
          onClick={handleSignOut}
          danger
        />
      </div>
    </div>
  );
};
